<template>
  <v-row justify="center">
    <v-dialog v-model="displayModal" max-width="800">
      <v-card class="rounded-lg">
        <v-toolbar dark>
          <v-btn icon dark @click="displayModal = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
          <v-toolbar-title>Bulk Outsource Cases</v-toolbar-title>
        </v-toolbar>

        <v-container fluid v-if="adapter">
          <v-btn x-small class="mt-2 rounded-lg" @click="partnerID = null">
            <v-icon x-small>mdi-arrow-left</v-icon>
            Back
          </v-btn>
          <Evident
            :partner="partnerID"
            v-if="adapter === 'EVIDENT'"
            @completed="displayModal = false"
          />
        </v-container>
        <v-container fluid v-if="!adapter">
          <v-form ref="outsourceForm" lazy-validation>
            <v-row class="mt-2">
              <v-col>
                <v-select
                  v-model="mainCase"
                  :items="caseItems"
                  item-text="text"
                  item-value="value"
                  :menu-props="{ bottom: true, offsetY: true }"
                  label="Main Case"
                  dense
                  outlined
                  class="rounded-lg"
                  :rules="[mainCaseValidation]"
                />
              </v-col>
            </v-row>
            <v-row>
              <v-col>
                <v-select
                  v-model="additionalCases"
                  :items="availableAdditionalCases"
                  item-text="text"
                  item-value="value"
                  :menu-props="{ bottom: true, offsetY: true }"
                  label="Additional Cases"
                  dense
                  multiple
                  outlined
                  class="rounded-lg"
                />
              </v-col>
            </v-row>
            <v-row class="mt-2">
              <v-col>
                <v-autocomplete
                  class="rounded-lg"
                  v-model="partnerID"
                  :rules="[partnerIDValidation]"
                  dense
                  outlined
                  :items="partnerItems"
                  label="Partner"
                />
              </v-col>
            </v-row>
            <v-row>
              <v-col>
                <v-text-field
                  class="rounded-lg"
                  outlined
                  dense
                  type="datetime-local"
                  label="Due Time"
                  v-model="dueTime"
                  :rules="[dueTimeValidation]"
                />
              </v-col>
            </v-row>
            <v-row>
              <v-col>
                <v-autocomplete
                  v-model="outsourceJobType"
                  :rules="[outsourceJobTypeValidation]"
                  dense
                  outlined
                  :items="jobTypes"
                  class="rounded-lg"
                  label="Job Type"
                />
              </v-col>
            </v-row>
            <v-row v-if="outsourceJobType === 'physical'">
              <v-col>
                <v-autocomplete
                  v-model="returningBranch"
                  :rules="[returningbranchValidation]"
                  dense
                  outlined
                  :items="branchItems"
                  class="rounded-lg"
                  label="Returning Branch"
                />
              </v-col>
            </v-row>
            <v-row>
              <v-col>
                <v-text-field
                  class="rounded-lg"
                  v-model="additionalRecipients"
                  outlined
                  dense
                  label="Additional Recipients"
                />
              </v-col>
            </v-row>
            <v-row>
              <v-col>
                <v-textarea
                  class="rounded-lg"
                  v-model="note"
                  :rules="[noteValidation]"
                  outlined
                  dense
                  label="Note"
                />
              </v-col>
            </v-row>
            <v-row>
              <v-col class="text-right">
                <v-btn
                  class="rounded-lg"
                  :disabled="submitting"
                  color="success"
                  @click="startOutsource()"
                >
                  <v-icon class="mr-2">mdi-send</v-icon>
                  Start
                </v-btn>
              </v-col>
            </v-row>
          </v-form>
        </v-container>
      </v-card>
    </v-dialog>
  </v-row>
</template>

<script>
import { DateTime } from "luxon";
import { mapGetters, mapActions } from "vuex";
import Evident from "@/collection/components/ComponentDrawer/Outsource/Adapters/Evident";

export default {
  name: "BulkOutsourceModal",
  components: {
    Evident,
  },
  props: {
    value: {
      type: Boolean,
      required: true,
    },
    prefilledMainCase: {
      type: Object,
      default: null,
    },
    prefilledAdditionalCases: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      submitting: false,
      mainCase: null,
      additionalCases: [],
      partnerID: null,
      dueTime: null,
      additionalRecipients: null,
      uploadNotRequired: false,
      note: null,
      outsourceJobType: "digital",
      jobTypes: [
        { text: "Digital Job", value: "digital" },
        { text: "Physical Job", value: "physical" },
      ],
    };
  },
  watch: {
    prefilledMainCase: {
      immediate: true,
      handler(value) {
        if (value) {
          this.mainCase = value;
        }
      },
    },
  },
  computed: {
    ...mapGetters([
      "partners",
      "branches",
      "collection",
      "outsourceModal",
      "kase",
    ]),
    displayModal: {
      get() {
        return this.value;
      },
      set(value) {
        this.triggerReload();
        this.$emit("input", value);
      },
    },

    dueTimeWithZone() {
      if (!this.dueTime) {
        return;
      }
      return DateTime.fromISO(this.dueTime).toISO();
    },
    caseItems() {
      if (!this.collection || !this.collection.cases) {
        return [];
      }
      return this.collection.cases.map((c) => {
        let text = `${c.case_mkey} - ${c.job_type}`;
        if (c.group_number) {
          text += ` - Group ${c.group_number}`;
        }
        return {
          text: text,
          value: c,
        };
      });
    },
    availableAdditionalCases() {
      return this.caseItems.filter((item) => item.value !== this.mainCase);
    },
    mainCaseValidation() {
      if (!this.mainCase) {
        return "Must select a main case";
      }
      return true;
    },
    partnerIDValidation() {
      if (!this.partnerID) {
        return "Must be selected";
      }
      return true;
    },
    outsourceJobTypeValidation() {
      if (!this.outsourceJobType) {
        return "Must be selected";
      }
      return true;
    },
    returningbranchValidation() {
      if (this.outsourceJobType === "physical" && !this.returningBranch) {
        return "Must be selected";
      }
      return true;
    },
    dueTimeValidation() {
      if (!this.dueTime) {
        return "Must be selected";
      }
      if (DateTime.now() > new Date(this.dueTime)) {
        return "Cannot be in the past";
      }
      return true;
    },
    noteValidation() {
      if (!this.note) {
        return "Must be provided";
      }
      return true;
    },
    partnerItems() {
      return this.partners
        .filter((partner) => {
          if (partner.adapter_type) {
            const hasMatchingCase =
              this.kase &&
              this.mainCase &&
              this.kase.case_id === this.mainCase.case_id;
            if (!hasMatchingCase) {
              return false;
            }
          }
          return true;
        })
        .map((partner) => {
          return {
            text: partner.name,
            value: partner.id,
          };
        });
    },
    branchItems() {
      return this.branches.map((branch) => {
        return {
          text: branch.name,
          value: branch.id,
        };
      });
    },
    returningBranch: {
      get() {
        return this.collection.fulfillment_branch_id;
      },
      set(value) {
        if (!value) {
          return;
        }
        this.collection.fulfillment_branch_id = value;
      },
    },
    adapter() {
      if (!this.partnerID) {
        return;
      }
      return this.partners.find((partner) => partner.id === this.partnerID)
        .adapter_type;
    },
  },
  methods: {
    ...mapActions(["startLoading", "stopLoading", "triggerReload"]),

    async startOutsource() {
      const allCases = [this.mainCase, ...this.additionalCases];
      const casesNotInDesign = [];
      allCases.forEach((c) => {
        if (!c.in_design) casesNotInDesign.push(c.case_mkey);
      });

      if (casesNotInDesign.length)
        if (
          !confirm(
            `These cases are not in design: ${casesNotInDesign.join(
              ", "
            )} - if you start this bulk outsource, the cases will be moved back to the design system. Are you sure this is okay?`
          )
        )
          return;

      if (!this.$refs.outsourceForm.validate()) return;

      try {
        this.submitting = true;
        this.startLoading();

        const additionalCaseIds = this.additionalCases
          .map((c) => c.case_id)
          .join(",");

        await this.$axios.post(`/api/v2/case/${this.mainCase.case_id}/`, {
          action: "start_bulk_outsource",
          partner_id: this.partnerID,
          due_time: this.dueTimeWithZone,
          additional_recipients: this.additionalRecipients,
          job_type: this.outsourceJobType,
          returning_branch_id: this.returningBranch,
          note: this.note,
          additional_case_ids: additionalCaseIds || null,
        });
      } catch (error) {
        console.error("Error while bulk outsourcing:", error);
      } finally {
        this.$refs.outsourceForm.reset();
        this.triggerReload();
        this.stopLoading();
        this.submitting = false;
        this.displayModal = false;
      }
    },
  },
  mounted() {},
};
</script>
